import React, { useState, useContext, useEffect } from "react";
import { Chart, Line } from "react-chartjs-2";
import "chartjs-plugin-annotation";
import moment from "moment";
import { UIContext } from "../../context/UIContext";
import { ChartDataContext } from "../../context/ChartDataContext";

import config from "../../dashboard-config";

import "./Timeseries.scss";

// This is a silly global instead of a state/context because making it a state is causing a big performance hit
// with the tooltip cursor hover performance.
// TODO : figure out how to do this right.
let hoveredTimeString = "";
// Time threshold between cursor and point to display in tool tip
const TT_TH_MINS = 2 * 60;

const Timeseries = ({
  endDate,
  graphName,
  observationData,
  observationTimestampData,
  optimalPointwiseData,
  optimalPointwiseTimestampData,
  selectedChart,
  selectedGraph,
  startDate
}) => {
  const graphUnits = {
    wave: {
      unit: "m",
      toFixed: 2
    },
    period: {
      unit: "s",
      stepSize: 4,
      toFixed: 2
    },
    spread: {
      unit: "°",
      max: 90,
      stepSize: 30,
      toFixed: 1
    },
    direction: {
      unit: "°",
      max: 360,
      stepSize: 90,
      toFixed: 1
    }
  };

  const [persistentStamp, setPersistentStamp] = useState(false);
  const { isTouchScreen } = useContext(UIContext);
  const {
    trailTimestamp,
    setSelectedDataPoint,
    setTrailTimestamp
  } = useContext(ChartDataContext);
  let observationNullspace, fullTimestamp, largest;

  // const fullTimestamp = observationTimestampData ? [...optimalPointwiseTimestampData, ...observationTimestampData, startDate, endDate]
  //     : [...optimalPointwiseTimestampData, startDate, endDate]
  // ;
  // const observationNullspace = observationData.map(record => null);
  const optimalPointWiseData = optimalPointwiseData.map(record =>
    record.value <= -999 ? null : record.value.toFixed(2)
  );
  const optimalPointwiseNullspace = optimalPointWiseData.map(record => null);
  if (observationData) {
    observationData = observationData.map(data =>
      data.toFixed(graphUnits[selectedGraph]["toFixed"])
    );
    fullTimestamp = [
      ...optimalPointwiseTimestampData,
      ...observationTimestampData,
      startDate,
      endDate
    ];
    observationNullspace = observationData.map(record => null);
    largest =
      Math.ceil(
        Math.max.apply(0, [...optimalPointWiseData, ...observationData])
      ) + 1;
  } else {
    fullTimestamp = [...optimalPointwiseTimestampData, startDate, endDate];
    observationNullspace = [];
    largest = Math.ceil(Math.max.apply(0, [...optimalPointWiseData])) + 1;
    observationData = [];
  }
  // let largest = observationData ?
  //     Math.ceil(Math.max.apply(0, [...optimalPointWiseData, ...observationData])) + 1 :
  //     Math.ceil(Math.max.apply(0, [...optimalPointWiseData])) + 1;

  const newLargest = largest % 2 === 0 ? largest : largest + 1;

  let chartRef = {};

  let observationalData = [
    {
      backgroundColor: "none",
      borderColor: "rgba(183,76,164,1)",
      borderWidth: 2,
      data: [...optimalPointWiseData, ...observationNullspace, null, null],
      fill: false,
      label: `${graphName} model`,
      lineTension: 0,
      radius: 0,
      spanGaps: false,
      pointBackgroundColor: "rgba(255,255,255,0)",
      pointBorderColor: "rgba(255,255,255,0)",
      pointHitRadius: () => {
        // TODO : scale pointHitRadius so it will grab +/- TT_TH_MINS
        return 20;
      }
    },
    {
      backgroundColor: "none",
      borderColor: "rgba(227,199,52,1)",
      borderWidth: 2,
      data: [...optimalPointwiseNullspace, ...observationData, null, null],
      fill: false,
      label: `${graphName} observations`,
      lineTension: 0,
      radius: 0,
      spanGaps: false,
      pointBackgroundColor: "rgba(255,255,255,0)",
      pointBorderColor: "rgba(255,255,255,0)",
      pointHitRadius: () => {
        // TODO : scale pointHitRadius so it will grab +/- TT_TH_MINS
        return 20;
      }
    }
  ];

  let stepSize =
    selectedChart.length > 1
      ? {
          min: 0,
          max: graphUnits[selectedGraph].max,
          stepSize: graphUnits[selectedGraph].stepSize
        }
      : {
          min: 0,
          max: newLargest,
          stepSize: newLargest <= 4 ? 1 : 2
        };

  useEffect(() => {
    let chartBackgroundPos = 60;
    if (isTouchScreen) chartBackgroundPos = 25;
    /* vertical line */
    Chart.pluginService.register({
      afterDraw: chart => {
        if (chart.tooltip._active && chart.tooltip._active.length) {
          const activePoint = chart.controller.tooltip._active[0];
          const ctx = chart.ctx;
          const x = activePoint._chart.tooltip._eventPosition.x;
          const topY = chart.scales["y-axis-0"].top;
          const bottomY = chart.scales["y-axis-0"].bottom;
          ctx.save();
          ctx.beginPath();
          ctx.moveTo(x, topY);
          ctx.lineTo(x, bottomY);
          ctx.lineWidth = 2;
          ctx.strokeStyle = "rgba(211, 231, 237, 0.5)";
          ctx.stroke();
          ctx.restore();
        }
      },
      beforeDraw: function(chart, easing) {
        if (
          chart.config.options.chartArea &&
          chart.config.options.chartArea.backgroundColor
        ) {
          var ctx = chart.chart.ctx;
          var chartArea = chart.chartArea;

          ctx.save();
          ctx.fillStyle = chart.config.options.chartArea.backgroundColor;
          ctx.fillRect(
            (chartArea.right - chartArea.left + chartBackgroundPos) / 2,
            chartArea.top,
            chartArea.right - chartArea.left,
            chartArea.bottom - chartArea.top
          );
          ctx.restore();
        }
      }
    });

    Chart.Tooltip.positioners.custom = function(elements, position) {
      if (!elements.length) {
        return false;
      }

      var offset = -100;

      //adjust the offset left or right depending on the event position
      if (elements[0]._chart.width / 2 > position.x) {
        offset = -100;
      } else {
        offset = -160;
      }

      return {
        x: position.x + offset,
        y: position.y
      };
    };
  }, [isTouchScreen]);

  const data = {
    labels: fullTimestamp,
    datasets: observationalData,
    yaxis: {
      labels: {
        style: {
          color: "rgba(255, 255, 255, 0.5)",
          fontSize: "10px"
        }
      }
    }
  };

  return (
    <div className="timeseries-graph-mobile">
      <Line
        ref={reference => (chartRef = reference)}
        data={data}
        options={{
          animation: false,
          annotation: {
            annotations: [
              {
                type: "line",
                mode: "vertical",
                scaleID: "x-axis-0",
                value: trailTimestamp,
                borderColor: `${
                  persistentStamp
                    ? "rgba(255, 255, 255, 0.15)"
                    : "rgba(0, 0, 0, 0)"
                }`,
                label: {
                  backgroundColor: "rgba(0,0,0,0)",
                  content: `${moment(trailTimestamp).format(
                    "MMM DD, YYYY - LT"
                  )} (${moment
                    .tz(
                      trailTimestamp,
                      Intl.DateTimeFormat().resolvedOptions().timeZone
                    )
                    .format("z")})`,
                  enabled: persistentStamp,
                  fontColor: "rgba(255, 255, 255, 0.7)",
                  position: "top"
                }
              }
            ]
          },
          chartArea: {
            backgroundColor: "rgba(21, 28, 49, 0.75)"
          },
          legend: {
            display: false,
            position: "left",
            labels: {
              fontColor: "rgba(255, 255, 255, 0.5)",
              padding: 15,
              usePointStyle: true
            }
          },
          tooltips: {
            mode: "x",
            intersect: false,
            enabled: false,
            position: "custom",
            xPadding: 10,
            yPadding: 10,
            callbacks: {
              title: () =>
                `${moment(hoveredTimeString).format("LT, MMM DD")} ${moment
                  .tz(
                    hoveredTimeString,
                    Intl.DateTimeFormat().resolvedOptions().timeZone
                  )
                  .format("z")}`,
              label: (tooltipItem, data) =>
                ` ${data.datasets[tooltipItem.datasetIndex].label}: ${
                  tooltipItem.value
                }${graphUnits[selectedGraph].unit}`,
              labelColor: function(tooltipItem, data) {
                var dataset =
                  data.config.data.datasets[tooltipItem.datasetIndex];
                return {
                  backgroundColor: dataset.borderColor
                };
              }
            },
            custom: tooltipModel => {
              // Tooltip Element
              var tooltipEl = document.getElementById("chartjs-tooltip");

              // Create element on first render
              if (!tooltipEl) {
                tooltipEl = document.createElement("div");
                tooltipEl.id = "chartjs-tooltip";
              }

              // Hide if no tooltip
              if (tooltipModel.opacity === 0) {
                tooltipEl.style.opacity = 0;
                tooltipEl.style.display = "none";
                return;
              }

              function getBody(bodyItem) {
                return bodyItem.lines;
              }

              // Set Text
              if (tooltipModel.body) {
                const waveObservationId = document.getElementById(
                  "wave-height-observations"
                );
                const peakPeriodId = document.getElementById(
                  "peak-period-observations"
                );
                const meanPeriodId = document.getElementById(
                  "mean-period-observations"
                );
                const peakDirectionId = document.getElementById(
                  "peak-direction-observations"
                );
                const meanDirectionId = document.getElementById(
                  "mean-direction-observations"
                );
                const peakSpreadId = document.getElementById(
                  "peak-spread-observations"
                );
                const meanSpreadId = document.getElementById(
                  "mean-spread-observations"
                );
                const waveModelId = document.getElementById(
                  "wave-height-model"
                );
                const meanModelId = document.querySelectorAll(
                  "[id*='mean-period-model']"
                );
                const meanDirectionModelId = document.querySelectorAll(
                  "[id*='mean-direction-model']"
                );
                const meanSpreadModelId = document.querySelectorAll(
                  "[id*='mean-spread-model']"
                );
                var bodyLines = tooltipModel.body.map(getBody);
                var dataPoints = tooltipModel.dataPoints;
                const datasetIndexValues = [];
                const pointsIndices = [];

                // NOTE : this assumes and requires that dataPoints are ordered by datasetIndex
                // then by label (time). Additionally labels and timestring must be in
                // lexicographically ordered format (eg ISO8061).
                let workingDSIndex = null;
                let minCandItem = null;
                let minCandIndex = -1;
                let maxCandItem = null;
                let maxCandIndex = -1;

                const processCandidates = () => {
                  let minCandDiff,
                    maxCandDiff = Infinity;
                  let selectedIndex = null;
                  if (workingDSIndex === null) return;
                  if (minCandItem)
                    minCandDiff = Math.abs(
                      moment(minCandItem.label).diff(
                        moment(hoveredTimeString),
                        "minutes"
                      )
                    );
                  if (maxCandItem)
                    maxCandDiff = Math.abs(
                      moment(maxCandItem.label).diff(
                        moment(hoveredTimeString),
                        "minutes"
                      )
                    );
                  if (minCandDiff <= TT_TH_MINS && minCandDiff < maxCandDiff)
                    selectedIndex = minCandIndex;
                  else if (maxCandDiff <= TT_TH_MINS)
                    selectedIndex = maxCandIndex;
                  if (selectedIndex) {
                    pointsIndices.push(selectedIndex);
                    datasetIndexValues.push(workingDSIndex);
                  }
                  minCandItem = null;
                  minCandIndex = -1;
                  maxCandItem = null;
                  maxCandIndex = -1;
                };

                dataPoints.forEach((item, index) => {
                  if (item.datasetIndex !== workingDSIndex) {
                    processCandidates();
                    workingDSIndex = item.datasetIndex;
                  }
                  if (item.label < hoveredTimeString) {
                    minCandItem = item;
                    minCandIndex = index;
                  } else if (
                    item.label >= hoveredTimeString &&
                    maxCandItem === null
                  ) {
                    maxCandItem = item;
                    maxCandIndex = index;
                  }
                });
                processCandidates();

                pointsIndices.forEach(value => {
                  const firstDigitInString = bodyLines[value][0].search(/\d/);
                  if (
                    bodyLines[value][0]
                      .toLowerCase()
                      .indexOf("significantwaveheight observation") !== -1
                  ) {
                    waveObservationId.innerHTML = bodyLines[value][0].substring(
                      firstDigitInString
                    );
                  } else if (
                    bodyLines[value][0]
                      .toLowerCase()
                      .indexOf("peakperiod observations") !== -1
                  ) {
                    peakPeriodId.innerHTML = bodyLines[value][0].substring(
                      firstDigitInString
                    );
                  } else if (
                    bodyLines[value][0]
                      .toLowerCase()
                      .indexOf("meanperiod observations") !== -1
                  ) {
                    meanPeriodId.innerHTML = bodyLines[value][0].substring(
                      firstDigitInString
                    );
                  } else if (
                    bodyLines[value][0]
                      .toLowerCase()
                      .indexOf("peakdirection observations") !== -1
                  ) {
                    peakDirectionId.innerHTML = bodyLines[value][0].substring(
                      firstDigitInString
                    );
                  } else if (
                    bodyLines[value][0]
                      .toLowerCase()
                      .indexOf("meandirection observations") !== -1
                  ) {
                    meanDirectionId.innerHTML = bodyLines[value][0].substring(
                      firstDigitInString
                    );
                  } else if (
                    bodyLines[value][0]
                      .toLowerCase()
                      .indexOf("peakdirectionalspread observations") !== -1
                  ) {
                    peakSpreadId.innerHTML = bodyLines[value][0].substring(
                      firstDigitInString
                    );
                  } else if (
                    bodyLines[value][0]
                      .toLowerCase()
                      .indexOf("meandirectionalspread observations") !== -1
                  ) {
                    meanSpreadId.innerHTML = bodyLines[value][0].substring(
                      firstDigitInString
                    );
                  } else if (
                    bodyLines[value][0]
                      .toLowerCase()
                      .indexOf("significantwaveheight model") !== -1 ||
                    bodyLines[value][0]
                      .toLowerCase()
                      .indexOf("waveheight model") !== -1
                  ) {
                    waveModelId.innerHTML = bodyLines[value][0].substring(
                      firstDigitInString
                    );
                  } else if (
                    bodyLines[value][0]
                      .toLowerCase()
                      .indexOf("period model") !== -1
                  ) {
                    meanModelId.forEach(item => {
                      item.innerHTML = bodyLines[value][0].substring(
                        firstDigitInString
                      );
                    });
                  } else if (
                    bodyLines[value][0]
                      .toLowerCase()
                      .indexOf("direction model") !== -1
                  ) {
                    meanDirectionModelId.forEach(item => {
                      item.innerHTML = bodyLines[value][0].substring(
                        firstDigitInString
                      );
                    });
                  } else if (
                    bodyLines[value][0]
                      .toLowerCase()
                      .indexOf("directionalspread model") !== -1 ||
                    bodyLines[value][0]
                      .toLowerCase()
                      .indexOf("spread model") !== -1
                  ) {
                    meanSpreadModelId.forEach(item => {
                      item.innerHTML = bodyLines[value][0].substring(
                        firstDigitInString
                      );
                    });
                  }
                });
              }
            }
          },
          maintainAspectRatio: false,
          scales: {
            xAxes: [
              {
                gridLines: {
                  display: false
                },
                ticks: {
                  display: false
                },
                type: "time",
                time: {
                  unit: "day"
                }
              }
            ],
            yAxes: [
              {
                gridLines: {
                  display: false
                },
                ticks: {
                  display: false,
                  ...stepSize
                }
              }
            ]
          },
          onClick: (event, i) => {
            const chartInstance = chartRef.chartInstance;
            const capturedTimeIndex = chartInstance.getElementsAtXAxis(event)[0]
              ._index;
            const capturedTimeString = fullTimestamp[capturedTimeIndex];
            console.log(`Selected time: ${capturedTimeString}`);
            setSelectedDataPoint(true);
            setTrailTimestamp(capturedTimeString);
            setPersistentStamp(true);
          },
          onHover: event => {
            const chartInstance = chartRef.chartInstance;
            const capturedTimeIndex = chartInstance.getElementsAtXAxis(event)[0]
              ._index;
            hoveredTimeString = fullTimestamp[capturedTimeIndex];
          }
        }}
      />
    </div>
  );
};

export default Timeseries;
